import React, { useState } from 'react';
import { Box, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ArticleIcon from '@mui/icons-material/Article';
import ReplayIcon from '@mui/icons-material/Replay';
import { ExtractionStatus, SUBMISSION_FILE_TYPE_LABEL, SubmissionFileType } from '../../../../model/ser/ser-enums';
import { ISubmissionDocumentResponse } from '../../../../model/ser/submission/response/submission-document-response';
import { formatFileSize } from '../../../../utils/format';
import { ExtractionStatusChip } from '../../components/submission-chips';
import { FileTypeIcon } from '../../components/file-type-icon';

interface SubmissionDocumentsProps {
    documents: ISubmissionDocumentResponse[];
    onDownload: (document: ISubmissionDocumentResponse) => Promise<void>;
    onViewContent: (document: ISubmissionDocumentResponse) => void;
    onReprocess: (document: ISubmissionDocumentResponse) => Promise<void>;
}

const REPROCESSABLE = [ExtractionStatus.Erro, ExtractionStatus.NaoSuportado];

export function SubmissionDocuments({ documents, onDownload, onViewContent, onReprocess }: SubmissionDocumentsProps) {
    // Ação em andamento por documento ("download-12", "reprocess-12")
    const [busy, setBusy] = useState<string>();

    async function track(key: string, work: () => Promise<void>) {
        setBusy(key);
        try {
            await work();
        } finally {
            setBusy(undefined);
        }
    }

    if (documents.length === 0) return <Typography variant="body2" color="text.secondary">Nenhum documento.</Typography>;

    return (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            {documents.map((doc, index) => {
                const meta = [
                    doc.fileType ? SUBMISSION_FILE_TYPE_LABEL[doc.fileType as SubmissionFileType] : undefined,
                    formatFileSize(doc.size) || undefined,
                    doc.pages ? `${doc.pages} ${doc.pages === 1 ? 'página' : 'páginas'}` : undefined,
                ].filter(Boolean).join(' · ');
                const canReprocess = REPROCESSABLE.includes(doc.extractionStatus as ExtractionStatus);
                return (
                    <Box key={doc.id} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', p: 1.5, borderTop: index ? '1px solid' : 'none', borderColor: 'divider' }}>
                        <FileTypeIcon type={doc.fileType} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600} noWrap title={doc.name}>{doc.name}</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.25 }}>
                                {meta && <Typography variant="caption" color="text.secondary">{meta}</Typography>}
                                <ExtractionStatusChip status={doc.extractionStatus} />
                            </Box>
                            {doc.extractionStatus === ExtractionStatus.Erro && doc.extractionError && (
                                <Typography variant="caption" color="error" component="div" sx={{ mt: 0.5 }}>
                                    {doc.extractionError}{doc.extractionAttempts ? ` (${doc.extractionAttempts} ${doc.extractionAttempts === 1 ? 'tentativa' : 'tentativas'})` : ''}
                                </Typography>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', flexShrink: 0 }}>
                            {canReprocess && (
                                <Tooltip title="Reprocessar extração">
                                    <span>
                                        <IconButton size="small" aria-label="Reprocessar extração" disabled={!!busy} onClick={() => track(`reprocess-${doc.id}`, () => onReprocess(doc))}>
                                            {busy === `reprocess-${doc.id}` ? <CircularProgress size={18} /> : <ReplayIcon fontSize="small" />}
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            )}
                            <Tooltip title={doc.hasContent ? 'Ver conteúdo' : 'Conteúdo ainda não disponível'}>
                                <span>
                                    <IconButton size="small" aria-label="Ver conteúdo" disabled={!doc.hasContent} onClick={() => onViewContent(doc)}>
                                        <ArticleIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title="Baixar arquivo">
                                <span>
                                    <IconButton size="small" aria-label={`Baixar ${doc.name}`} disabled={!!busy} onClick={() => track(`download-${doc.id}`, () => onDownload(doc))}>
                                        {busy === `download-${doc.id}` ? <CircularProgress size={18} /> : <DownloadIcon fontSize="small" />}
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
}
